import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Lock, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminLogin() {
  const [formData, setFormData] = useState({
    login: '', // Can be email or mobile
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Clear old tokens before login
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');

    try {
      // Validate input
      if (!formData.login.trim() || !formData.password.trim()) {
        throw new Error('กรุณากรอกข้อมูลให้ครบถ้วน');
      }

      // Determine if login is email or mobile
      const isEmail = formData.login.includes('@');
      const requestData = isEmail 
        ? { email: formData.login, password: formData.password }
        : { mobile: formData.login, password: formData.password };

      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }

      if (!data.success) {
        throw new Error(data.error || 'การเข้าสู่ระบบไม่สำเร็จ');
      }

      // Check if user has admin permissions
      if (!data.data.user?.permissions || !data.data.user.permissions.includes('admin:manage')) {
        throw new Error('คุณไม่มีสิทธิ์เข้าถึงระบบ Admin');
      }

      // Store authentication data
      localStorage.setItem('authToken', data.data.token);
      localStorage.setItem('userData', JSON.stringify({
        id: data.data.user.id,
        email: data.data.user.email,
        mobile: data.data.user.mobile || data.data.user.phone,
        businessName: data.data.user.shop_name || data.data.user.name,
        permissions: data.data.user.permissions
      }));

      toast.success('เข้าสู่ระบบสำเร็จ');
      navigate('/admin/kyc');

    } catch (error: any) {
      console.error('Login error:', error);
      setError(error.message || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ');
      toast.error(error.message || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError(''); // Clear error when user starts typing
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            เข้าสู่ระบบ Admin
          </h2>
          <p className="text-gray-600">
            จัดการระบบยืนยันตัวตน (KYC)
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-center gap-2">
              <Lock className="w-5 h-5" />
              Admin Login
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="login" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  อีเมลหรือเบอร์โทรศัพท์
                </Label>
                <div className="relative">
                  <Input
                    id="login"
                    name="login"
                    type="text"
                    value={formData.login}
                    onChange={handleInputChange}
                    placeholder="admin@example.com หรือ 0812345678"
                    className="pl-10"
                    required
                    disabled={loading}
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {formData.login.includes('@') ? (
                      <Mail className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Phone className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  รหัสผ่าน
                </Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="รหัสผ่าน"
                  required
                  disabled={loading}
                />
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
              >
                {loading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
              </Button>
            </form>

            {/*<div className="mt-4 text-center text-sm text-gray-500">
              <p>สำหรับผู้ดูแลระบบเท่านั้น</p>
              <p className="mt-2">
                <strong>ทดสอบ:</strong> admin@slipaj.com / 0888888888 
                <br />
                <strong>รหัสผ่าน:</strong> Admin@123456
              </p>
            </div>
            */}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}